import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ADD_TO_WISHLIST, REMOVE_FROM_WISHLIST } from '../../../../constants/messages';
import { AppDispatch, RootState } from '../../../../store/store';
import { wishListHandler } from '../../../../store/UserSlice';
import { CartItem, Product } from '../../../../types/common';
import Section from '../../../layouts/showcaseLayouts/Section/Section';
import SectionBody from '../../../layouts/showcaseLayouts/Section/SectionBody/SectionBody';
import AddToCartBtn from '../../../showcase/AddToCartBtn/AddToCartBtn';
import Chip from '../../../UI/Chip/Chip';
import IconButton from '../../../UI/IconButton/IconButton';
import FavoriteIcon from '../../../UI/icons/FavoriteIcon/FavoriteIcon';
import NotFound from '../NotFound/NotFound';
import InfoBlock from './InfoBlock/InfoBlock';
import classes from './ProductPage.module.css';
import { fetchProductBySlug } from '../../../../store/ProductSlice';
import { Helmet } from 'react-helmet-async';
import { buildFillerTypeFilterUrl, buildFabricTypeFilterUrl } from '../../../../utils/helpers';

interface IProductPageProps {}

const getImageUrl = (imageId: string) => `${process.env.REACT_APP_SERVER_URL}/products/images/${imageId}`;

const parseCareInstructions = (instructions: string): React.ReactNode => {
    // Ищем все вхождения чисел с точкой, например "1.", "2.", "3."
    const matches = Array.from(instructions.matchAll(/(\d+)\./g));
    // Если не найдено ни одного вхождения, возвращаем исходный текст
    if (matches.length === 0) {
        return <p style={{ paddingLeft: '1rem' }}>{instructions}</p>;
    }
    // Проверяем, что последовательность начинается с 1 и идет подряд (1, 2, 3, ...)
    let isValidSequence = true;
    for (let i = 0; i < matches.length; i++) {
        const number = parseInt(matches[i][1], 10);
        if (number !== i + 1) {
            isValidSequence = false;
            break;
        }
    }
    // Если последовательность нарушена, возвращаем исходный текст
    if (!isValidSequence) {
        return <p style={{ paddingLeft: '1rem' }}>{instructions}</p>;
    }
    // Если последовательность валидна, разбиваем строку по шаблону "цифра. "
    const parts = instructions.split(/\s*\d+\.\s*/).filter(Boolean);
    return parts.map((part, index) => (
        <p key={index} style={{ paddingLeft: '1rem' }}>
            <strong>{index + 1}.</strong> {part.trim()}
        </p>
    ));
};

const looksLikeObjectId = (s?: string) => Boolean(s && /^[a-fA-F0-9]{24}$/.test(s!));

const ProductPage: React.FC<IProductPageProps> = () => {
    const { slug: slugParam, id: productIdParam } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { products, isLoading } = useSelector((state: RootState) => state.product);
    const { wishlist } = useSelector((state: RootState) => state.user);
    const productBySlug = slugParam ? products.find((p) => p.slug === slugParam) : undefined;
    const productById = productIdParam ? products.find((p) => p.id === productIdParam) : undefined;
    const product = (productBySlug || productById) as Product | undefined;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [activeSpecifications, setActiveSpecifications] = useState(true);
    // const [activeRawMaterial, setActiveRawMaterial] = useState(false);
    const [activeDescription, setActiveDescription] = useState(true);
    const [activeCare, setActiveCare] = useState(false);
    const [activeReviews, setActiveReviews] = useState(false);
    const [activeSellerName, setActiveSellerName] = useState(false);

    useEffect(() => {
        setCurrentIndex(0);
    }, [slugParam, productIdParam]);
    // если пришли по slug и товара нет в сторе — подгрузить его
    useEffect(() => {
        if (slugParam && looksLikeObjectId(slugParam)) return;
        if (slugParam && !productBySlug && !isLoading) {
            dispatch(fetchProductBySlug(slugParam));
        }
    }, [slugParam, productBySlug, isLoading, dispatch]);
    // если пришли по старому id-URL — редиректнуть на slug, когда продукт найден
    useEffect(() => {
        if (!slugParam && productById?.slug) {
            navigate(`/${productById.slug}`, { replace: true });
        }
    }, [slugParam, productById, navigate]);

    if (!product) {
        return <NotFound />;
    }

    const groupId = product.group?.id;
    const allVariants: Product[] = groupId ? products.filter((p) => p.group?.id === groupId) : [];

    const {
        id,
        slug,
        name,
        nameDescription,
        description,
        fillerType,
        fabricType,
        price,
        weight,
        discount,
        specialDiscount,
        // quantity,
        countryOrigin,
        brandName,
        articleNumber,
        category,
        sellerName,
        productСolor,
        rawMaterial,
        careInstructions,
        complectation,
        group,
    } = product;
    const isWished = wishlist.includes(id);
    const images = [
        getImageUrl(product.image),
        product.secondImage ? getImageUrl(product.secondImage) : null,
        product.thirdImage ? getImageUrl(product.thirdImage) : null,
        product.fourthImage ? getImageUrl(product.fourthImage) : null,
        product.fifthImage ? getImageUrl(product.fifthImage) : null,
        product.sixthImage ? getImageUrl(product.sixthImage) : null,
    ].filter(Boolean) as string[];

    const effectiveUnitPrice = specialDiscount?.discountedPrice ?? discount?.discountedPrice ?? price;
    const cartItem: CartItem = {
        productId: id,
        name,
        nameDescription,
        quantity: 1,
        price: effectiveUnitPrice,
        totalPrice: effectiveUnitPrice,
        weight,
        totalWeight: weight,
        discountedPrice: discount?.discountedPrice,
        discount: discount?.percent,
        specialDiscountPercent: specialDiscount?.percent,
        specialDiscountedPrice: specialDiscount?.discountedPrice,
    };

    const handleVariantClick = (variant: Product) => {
        if (variant.id === product.id) return;
        navigate(`/${variant.slug}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const pageUrl = `${origin}/${slug}`;
    const fillerTypeFilterHref = buildFillerTypeFilterUrl(fillerType);
    const fabricTypeFilterHref = buildFabricTypeFilterUrl(fabricType);

    return (
        <Section>
            <>
                <Helmet>
                    <title>
                        {name} — {fillerType?.name || 'S-Gabai'}
                    </title>
                    <link rel="canonical" href={pageUrl} />
                    <meta property="og:type" content="product" />
                    <meta property="og:title" content={name} />
                    <meta property="og:url" content={pageUrl} />
                    {product.image && <meta property="og:image" content={`${process.env.REACT_APP_SERVER_URL}/products/images/${product.image}`} />}
                    <script type="application/ld+json">
                        {JSON.stringify({
                            '@context': 'https://schema.org/',
                            '@type': 'Product',
                            name: name,
                            sku: articleNumber || undefined,
                            fillerType: fillerType?.name || undefined,
                            fabricType: fabricType?.name || undefined,
                            image: product.image ? [`${process.env.REACT_APP_SERVER_URL}/products/images/${product.image}`] : undefined,
                            offers: {
                                '@type': 'Offer',
                                priceCurrency: 'RUB',
                                price: (specialDiscount?.discountedPrice ?? discount?.discountedPrice ?? price).toString(),
                                availability: 'https://schema.org/InStock',
                                url: pageUrl,
                            },
                        })}
                    </script>
                </Helmet>
                <SectionBody>
                    <>
                        <div className={classes['product-page']}>
                            <button className={classes['back-button']} onClick={handleBackClick}>
                                Назад
                            </button>

                            <div className={classes.imageBox}>
                                {images.length > 1 && (
                                    <button className={`${classes.arrowButton} ${classes.arrowButtonPrev}`} onClick={handlePrev} title="Предыдущее фото">
                                        &#8249;
                                    </button>
                                )}
                                <img
                                    src={images[currentIndex]}
                                    alt={`Product ${currentIndex + 1}`}
                                    className={`${classes.image} ${currentIndex === 0 ? '' : classes['image-hidden']}`}
                                />
                                {images.length > 1 && (
                                    <button className={`${classes.arrowButton} ${classes.arrowButtonNext}`} onClick={handleNext} title="Следующее фото">
                                        &#8250;
                                    </button>
                                )}
                                {images.length > 1 && (
                                    <div className={classes['indicators']}>
                                        {images.map((_, index) => (
                                            <span key={index} className={`${classes['indicator']} ${index === currentIndex ? classes['active'] : ''}`} />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* <div className={classes['image-wrapper']}>
                <img src={getImageUrl(product.image)} alt={name} className={classes.image} />
              </div> */}
                            <div className={classes['content-wrapper']}>
                                <div className={classes['title-wrapper']}>
                                    <h1 className={classes.title} title={name}>
                                        {name}
                                    </h1>
                                    <p className={classes.nameDescription} title={nameDescription}>
                                        {nameDescription}
                                    </p>
                                    <div className={classes['chip-wrapper']}>
                                        {fabricType && <Chip text={fabricType.name} url={fabricTypeFilterHref || undefined} mode={'highlighted'} />}
                                        {fillerType && <Chip text={fillerType.name} url={fillerTypeFilterHref || undefined} mode={'plain'} />}
                                        {/* <Chip text={`В наличии ${quantity} шт`} mode={'plain'} /> */}
                                        {articleNumber && <Chip text={articleNumber} title={'Артикул товара'} mode={'plain'} />}
                                    </div>
                                </div>

                                <IconButton onClick={() => dispatch(wishListHandler({ id, isWished }))} column position="absolute-prodPage-favor">
                                    <>
                                        <FavoriteIcon filled={isWished} height={30} width={30} />
                                        <span className={classes['wishlist-text']}>{isWished ? REMOVE_FROM_WISHLIST : ADD_TO_WISHLIST}</span>
                                    </>
                                </IconButton>

                                <div className={classes['price-wrapper']}>
                                    {specialDiscount ? (
                                        <span className={`${classes.price}`}>
                                            <span className={classes.price}>{specialDiscount.discountedPrice} ₽</span>
                                            <span className={classes['old-price']}>{price} ₽</span>
                                            <Chip text={'-' + specialDiscount.percent + '%'} mode={'attention'} />
                                        </span>
                                    ) : discount ? (
                                        <span className={`${classes.price}`}>
                                            <span className={classes.price}>{discount.discountedPrice} ₽</span>
                                            <span className={classes['old-price']}>{price} ₽</span>
                                            <Chip text={'-' + discount.percent + '%'} mode={'attention'} />
                                        </span>
                                    ) : (
                                        <span className={classes.price}>{price} ₽</span>
                                    )}

                                    <div className={classes.actions}>
                                        <AddToCartBtn product={cartItem} />
                                    </div>
                                </div>

                                <div className={classes['description-wrapper']}>
                                    <div className={`${classes.description} ${activeSpecifications ? classes.description_active : ''}`}>
                                        <p className={classes.subtitle} onClick={() => setActiveSpecifications(!activeSpecifications)}>
                                            О товаре
                                            <span className={`${classes.arrow} ${activeSpecifications ? classes.arrow_open : ''}`}>&#8249;</span>
                                        </p>
                                        {category && category.name && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>Категория</span>
                                                <span className={classes.specificationsBox_value}>{category.name}</span>
                                            </div>
                                        )}
                                        {articleNumber && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>Артикул</span>
                                                <span className={classes.specificationsBox_value}>{articleNumber}</span>
                                            </div>
                                        )}

                                        {allVariants.length > 0 && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>{group?.url}</span>
                                                <div className={classes.variantRow}>
                                                    {allVariants.map((v) => {
                                                        const isActive = v.id === product.id;
                                                        return (
                                                            <button
                                                                key={v.id}
                                                                className={`${classes.variantBtn} ${isActive ? classes.variantBtnActive : ''}`}
                                                                onClick={() => handleVariantClick(v)}
                                                                disabled={isActive}
                                                            >
                                                                {v.group?.optionValue || 'Вариант'}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* {sellerName && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>Продавец</span>
                                                <span className={classes.specificationsBox_value}>{sellerName}</span>
                                            </div>
                                        )} */}
                                        {sellerName && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>Состав</span>
                                                <span className={classes.specificationsBox_value}>{rawMaterial}</span>
                                            </div>
                                        )}
                                        {/* {countryOrigin && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>Страна-изготовитель</span>
                                                <span className={classes.specificationsBox_value}>{countryOrigin}</span>
                                            </div>
                                        )} */}
                                        {complectation && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>Комплектация</span>
                                                <span className={classes.specificationsBox_value}>{complectation}</span>
                                            </div>
                                        )}
                                        {productСolor && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>Дизайн, цвет</span>
                                                <span className={classes.specificationsBox_value}>{productСolor}</span>
                                            </div>
                                        )}
                                        {weight && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>Вес товара</span>
                                                <span className={classes.specificationsBox_value}>{weight} кг</span>
                                            </div>
                                        )}
                                        {brandName && (
                                            <div className={classes.specificationsBox}>
                                                <span className={classes.specificationsBox_title}>Торговая марка</span>
                                                <span className={classes.specificationsBox_value}>{brandName}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* {rawMaterial && (
                                        <div className={`${classes.description} ${activeRawMaterial ? classes.description_active : ''}`}>
                                            <p className={classes.subtitle} onClick={() => setActiveRawMaterial(!activeRawMaterial)}>
                                                Состав
                                                <span className={`${classes.arrow} ${activeRawMaterial ? classes.arrow_open : ''}`}>&#8249;</span>
                                            </p>
                                            <p className={`${classes.text}`}>{rawMaterial}</p>
                                        </div>
                                    )} */}

                                    {description && (
                                        <div
                                            className={`${classes.description} ${activeDescription ? classes.description_active : ''} ${classes.descriptionMobile}`}
                                        >
                                            <p className={classes.subtitle} onClick={() => setActiveDescription(!activeDescription)}>
                                                Описание
                                                <span className={`${classes.arrow} ${activeDescription ? classes.arrow_open : ''}`}>&#8249;</span>
                                            </p>
                                            <p className={`${classes.text}`}>{description}</p>
                                        </div>
                                    )}

                                    {careInstructions && (
                                        <div className={`${classes.description} ${activeCare ? classes.description_active : ''} ${classes.descriptionMobile}`}>
                                            <p className={classes.subtitle} onClick={() => setActiveCare(!activeCare)}>
                                                Уход
                                                <span className={`${classes.arrow} ${activeCare ? classes.arrow_open : ''}`}>&#8249;</span>
                                            </p>
                                            <div className={`${classes.text}`}>{parseCareInstructions(careInstructions)}</div>
                                        </div>
                                    )}

                                    {(sellerName || countryOrigin) && (
                                        <div className={`${classes.description} ${activeSellerName ? classes.description_active : ''} ${classes.descriptionMobile}`}>
                                            <p className={classes.subtitle} onClick={() => setActiveSellerName(!activeSellerName)}>
                                                Информация о продавце
                                                <span className={`${classes.arrow} ${activeSellerName ? classes.arrow_open : ''}`}>&#8249;</span>
                                            </p>
                                            <p className={`${classes.text}`}>
                                                {sellerName && `Продавец: ${sellerName}.`}
                                                <br />
                                                {countryOrigin && `Страна-изготовитель: ${countryOrigin}.`}
                                            </p>
                                        </div>
                                    )}

                                    <div className={`${classes.description} ${activeReviews ? classes.description_active : ''} ${classes.descriptionMobile}`}>
                                        <p className={classes.subtitle} onClick={() => setActiveReviews(!activeReviews)}>
                                            Отзывы
                                            <span className={`${classes.arrow} ${activeReviews ? classes.arrow_open : ''}`}>&#8249;</span>
                                        </p>
                                        <p className={`${classes.text}`}>Отзывы о товаре</p>
                                    </div>
                                </div>

                                <InfoBlock popupStyles />
                            </div>
                        </div>
                        <div className={classes['description-wrapper-desktop']}>
                            {description && (
                                <div className={`${classes.description} ${activeDescription ? classes.description_active : ''}`}>
                                    <p className={classes.subtitle} onClick={() => setActiveDescription(!activeDescription)}>
                                        Описание
                                        <span className={`${classes.arrow} ${activeDescription ? classes.arrow_open : ''}`}>&#8249;</span>
                                    </p>
                                    <p className={`${classes.text}`}>{description}</p>
                                </div>
                            )}

                            {careInstructions && (
                                <div className={`${classes.description} ${activeCare ? classes.description_active : ''}`}>
                                    <p className={classes.subtitle} onClick={() => setActiveCare(!activeCare)}>
                                        Уход
                                        <span className={`${classes.arrow} ${activeCare ? classes.arrow_open : ''}`}>&#8249;</span>
                                    </p>
                                    <div className={`${classes.text}`}>{parseCareInstructions(careInstructions)}</div>
                                </div>
                            )}

                            {(sellerName || countryOrigin) && (
                                <div className={`${classes.description} ${activeSellerName ? classes.description_active : ''}`}>
                                    <p className={classes.subtitle} onClick={() => setActiveSellerName(!activeSellerName)}>
                                        Информация о продавце
                                        <span className={`${classes.arrow} ${activeSellerName ? classes.arrow_open : ''}`}>&#8249;</span>
                                    </p>
                                    <p className={`${classes.text}`}>
                                        {sellerName && `Продавец: ${sellerName}.`}
                                        <br />
                                        {countryOrigin && `Страна-изготовитель: ${countryOrigin}.`}
                                    </p>
                                </div>
                            )}

                            <div className={`${classes.description} ${activeReviews ? classes.description_active : ''}`}>
                                <p className={classes.subtitle} onClick={() => setActiveReviews(!activeReviews)}>
                                    Отзывы
                                    <span className={`${classes.arrow} ${activeReviews ? classes.arrow_open : ''}`}>&#8249;</span>
                                </p>
                                <p className={`${classes.text}`}>Отзывы о товаре</p>
                            </div>
                        </div>
                    </>
                </SectionBody>
            </>
        </Section>
    );
};

export default ProductPage;
